import React, { useMemo } from 'react';
import type { ConsensusTableWidgetInstanceConfig, ConsensusTableData } from './types';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { AgGridWrapper } from '../../../blocks/tables';
import type { ColDef } from 'ag-grid-community';

// Sample data shown during dashboard construction (no asset assigned)
const SAMPLE_DATA: ConsensusTableData = {
  consensus_data: [
    { LineItem: 'Revenue', '1Q24': '94.8B', '2Q24': '97.1B', 'FY2024': '385.0B', 'FY2025': '410.2B' },
    { LineItem: 'EBITDA', '1Q24': '35.2B', '2Q24': '36.8B', 'FY2024': '142.0B', 'FY2025': '155.0B' },
    { LineItem: 'EPS', '1Q24': '1.53', '2Q24': '1.61', 'FY2024': '6.25', 'FY2025': '6.82' },
    { LineItem: 'Net Income', '1Q24': '23.6B', '2Q24': '24.8B', 'FY2024': '96.0B', 'FY2025': '105.0B' },
  ],
};

interface Props {
  config: ConsensusTableWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const ConsensusTableWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<ConsensusTableData>(
    'consensus_table',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;

  // Build column definitions dynamically from the data shape
  const columnDefs = useMemo<ColDef[]>(() => {
    if (!displayData?.consensus_data?.length) return [];
    const keys = Object.keys(displayData.consensus_data[0]);
    const [firstCol, ...periodCols] = keys;

    return [
      {
        field: firstCol,
        headerName: firstCol,
        pinned: 'left' as const,
        sortable: false,
        filter: false,
        cellClass: 'font-medium text-neutral-800',
        minWidth: 120,
      },
      ...periodCols.map((col) => ({
        field: col,
        headerName: col,
        sortable: false,
        filter: false,
        type: 'rightAligned' as const,
        cellClass: 'text-neutral-600',
        flex: 1,
      })),
    ];
  }, [displayData]);

  if (loading) return <WidgetLoadingState size="sm" />;
  if (error) return <WidgetErrorState message={error} onRetry={refetch} />;
  if (!displayData?.consensus_data?.length) return null;

  return (
    <div className={`h-full flex flex-col overflow-hidden bg-surface ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}
      <div className="flex-1 overflow-hidden">
        <AgGridWrapper
          rowData={displayData.consensus_data}
          columnDefs={columnDefs}
          rowHeight={36}
          headerHeight={32}
          suppressCellFocus
          domLayout="autoHeight"
        />
      </div>
    </div>
  );
};

export default ConsensusTableWidgetRenderer;
